import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import ClockLoader from 'shared/components/ClockLoader';
import { resolveRegistryTenantId } from 'shared/api/resolveRegistryTenantId';
import { fetchSessionContext } from 'shared/api/sessionContext';
import EntityFormFields from '../../../shared/EntityFormFields';
import {
  apiErrorMessage,
  isEmptyFieldValue,
  unwrapCreatedEntity,
} from '../../../utils/responseHelpers';

const WizardLookupCreateDialog = ({
  open,
  onClose,
  createConfig,
  saveLabel = 'Save & select',
  onCreated,
}) => {
  const [values, setValues] = useState({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const initialValues = useMemo(
    () => createConfig?.getInitialValues?.(null, createConfig.fields) ?? {},
    [createConfig]
  );

  useEffect(() => {
    if (!open) return;
    setValues(initialValues);
    setError('');
    setCreating(false);
  }, [open, initialValues]);

  const setFieldValue = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async () => {
    if (!createConfig) return;

    const required = createConfig.fields.filter((f) => f.required);
    for (const f of required) {
      if (isEmptyFieldValue(f, values[f.name])) {
        setError(`${f.label} is required`);
        return;
      }
    }

    if (!resolveRegistryTenantId()) {
      setError(
        'No tenant is selected. Log in with your organization account (tenant login URL) so the app can send X-TenantID.'
      );
      return;
    }

    setCreating(true);
    setError('');
    try {
      await fetchSessionContext();
      const res = await createConfig.api.create(createConfig.toPayload(values));
      const created = unwrapCreatedEntity(res);
      if (!created?.id) {
        setError('Record was saved but the server did not return an id. Refresh the page and try again.');
        return;
      }
      onCreated?.(created, values);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to create record. Please try again.'));
    } finally {
      setCreating(false);
    }
  };

  if (!createConfig) return null;

  const entityLabel = createConfig.entityName ?? 'record';

  return (
    <Dialog open={open} onClose={creating ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
        Add {entityLabel}
      </DialogTitle>
      <DialogContent>
        <EntityFormFields
          fields={createConfig.fields}
          values={values}
          errors={{}}
          touched={{}}
          handleChange={(e) => setFieldValue(e.target.name, e.target.value)}
          handleBlur={() => {}}
          setFieldValue={setFieldValue}
          columns={2}
          dense
        />
        {error ? (
          <Typography variant="caption" color="error" display="block" mt={1.5}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={creating}>
          Cancel
        </Button>
        <Button variant="contained" color="secondary" onClick={handleSubmit} disabled={creating}>
          {creating ? <ClockLoader inline size={22} /> : saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WizardLookupCreateDialog;
